import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SearchBox from '../../components/SearchBox/SearchBox';
import PopularKeywords from '../../components/PopularKeywords/PopularKeywords';
import { searchAPI } from '../../services/api';
import { formatNumber } from '../../utils/helpers';
import styles from './Home.module.css';

const Home = () => {
  const { isAuthenticated } = useAuth();
  const [latestResources, setLatestResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [displayCount, setDisplayCount] = useState(0);
  const [diskSources, setDiskSources] = useState([]);
  const [selectedDiskSource, setSelectedDiskSource] = useState('');

  useEffect(() => {
    fetchLatestResources();
    fetchDiskSources();
  }, []);

  const fetchDiskSources = async () => {
    try {
      const response = await searchAPI.getDiskSources();
      setDiskSources(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch disk sources:', error);
    }
  };

  const fetchLatestResources = async () => {
    try {
      const response = await searchAPI.getLatestResources(33);
      const data = response.data.data || {};
      setLatestResources(data.records || []);
      setTotalCount(data.total || 0);
      setDisplayCount(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch latest resources:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (totalCount === 0) return;

    let timeoutId;
    const scheduleNextUpdate = () => {
      // 随机延迟 1 到 4 秒
      const delay = Math.floor(Math.random() * 3000) + 1000;
      timeoutId = setTimeout(() => {
        // 随机增加 1 到 8 个资源
        const increment = Math.floor(Math.random() * 8) + 1;
        setDisplayCount(prev => prev + increment);
        scheduleNextUpdate();
      }, delay);
    };

    scheduleNextUpdate();

    return () => clearTimeout(timeoutId);
  }, [totalCount]);

  return (
    <>
      <Helmet>
        <title>网盘搜索-找网盘资源就上万盘搜-聚合网盘搜索引擎</title>
        <meta name="description" content="万盘搜是一个专业的网盘资源搜索引擎，旨在为用户提供一个免费、高效的网盘资源搜索平台。用户可以通过万盘搜快速找到存储在各大网盘上的资源，包括但不限于阿里云盘、百度网盘、夸克网盘、迅雷网盘、UC网盘、蓝奏云盘和115网盘等，万盘搜以其强大的搜索能力和简洁的用户界面，成为了寻找网盘资源的有力工具。" />
        <meta name="keywords" content="万盘搜,万盘搜索,网盘搜索,网盘资源" />
        <link rel="canonical" href="https://wpansou.aicosmos.cn/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "万盘搜",
            "url": "https://wpansou.aicosmos.cn",
            "description": "专业的网盘资源搜索引擎",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://wpansou.aicosmos.cn/search?keyword={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>
      <div className={styles.home}>
        <div className="container">
          <div className={styles.homeContent}>
            <div className={styles.heroSection}>
              <h1 className={styles.heroTitle}>万盘搜</h1>
              <p className={styles.heroSubtitle}>
                搜资源，找万盘，想要什么都不难！
              </p>
              {displayCount > 0 && (
                <div className={styles.liveCount}>
                  已收录资源 <span className={styles.countNumber}>{displayCount.toLocaleString('en-US')}</span> 个
                </div>
              )}
            </div>

            <div className={styles.searchSection}>
              <div className={styles.diskSourceTabs}>
                <button
                  className={`${styles.diskSourceTab} ${selectedDiskSource === '' ? styles.active : ''}`}
                  onClick={() => setSelectedDiskSource('')}
                >
                  全部网盘
                </button>
                {diskSources.map((source) => (
                  <button
                    key={source.source}
                    className={`${styles.diskSourceTab} ${selectedDiskSource === source.source ? styles.active : ''}`}
                    onClick={() => setSelectedDiskSource(source.source)}
                  >
                    {source.label}
                  </button>
                ))}
              </div>
              <SearchBox
                placeholder={`输入关键词搜索${displayCount > 0 ? formatNumber(displayCount) : ''}个网盘资源...`}
                className={styles.homeSearch}
                diskSource={selectedDiskSource}
              />
            </div>

            <PopularKeywords limit={10} />

              <div className={styles.promoSection}>
                <div className={styles.promoCard}>
                  <div className={styles.promoIcon}>🎁</div>
                  <div className={styles.promoContent}>
                    <h3 className={styles.promoTitle}>注册即送20金币</h3>
                    <p className={styles.promoDescription}>
                      立即注册账号，免费领取20金币，开启你的资源探索之旅！
                    </p>
                  </div>
                  <Link to="/register" className={styles.promoButton}>
                    立即注册
                  </Link>
                </div>

                <div className={styles.promoCard}>
                  <div className={styles.promoIcon}>🎉</div>
                  <div className={styles.promoContent}>
                    <h3 className={styles.promoTitle}>邀请好友赚金币</h3>
                    <p className={styles.promoDescription}>
                      每邀请一位好友注册即送10金币，多邀多得，上不封顶！
                    </p>
                  </div>
                  <Link to="/profile" className={styles.promoButton}>
                    去邀请
                  </Link>
                </div>
              <div className={styles.promoCard}>
                <div className={styles.promoIcon}>📅</div>
                <div className={styles.promoContent}>
                  <h3 className={styles.promoTitle}>每日签到得金币</h3>
                  <p className={styles.promoDescription}>
                    每天签到即得2金币，坚持签到享受更多福利！
                  </p>
                </div>
                <Link to="/profile" className={styles.promoButton}>
                  立即签到
                </Link>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default Home;